/* eslint-disable no-shadow */
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { get as getProp } from 'lodash';
import { useParams, useLocation, useHistory, Redirect } from 'react-router-dom';
import { Modal, Form, Input } from 'antd';
import Loader from '../../common/components/Loader';
import Details from './Details';
import Status from './Status';
import AuditTrail from './AuditTrail';
import Portlet from '../../common/components/Portlet';
import BulkActionsWorkflowModal from '../../common/components/Modal/BulkActionsWorkflow';
import { formToQueryString } from '../../common/lib/transformSearchRequest';
import SingleActionsWorkflowModal from '../../common/components/Modal/SingleActionsWorkflow';
import {
  fetchUser,
  fetchUsers,
  clearError,
  saveAuditTrailComment,
  setIsSingleUserWorkflow,
  resetAuditTrailPagination,
  fetchEventHistory,
  resetLoyaltyUser,
  resetEventLot,
} from '../../redux/actions';
import {
  setCommentFocusIntervalRef,
  clearIntervalRef,
} from '../../common/lib/utils';
import './index.css';

import EventLog from './EventLog';

const mapStateToProps = state => ({
  isWorkflowActive: state.bulkActionsReducer.isActive,
  user: state.usersReducer.user,
  userDbId: getProp(state.usersReducer.user, 'id', ''),
  userSsoId: getProp(state.usersReducer.user, 'sub', ''),
  // TODO Create a 404 page for user?
  errorCode: state.usersReducer.errorCode,
  isFetching: !state.usersReducer.user || state.usersReducer.isFetching,
  searchParams: state.usersReducer.searchParams,
  auditTrailLimit: state.usersReducer.auditTrailLimit,
  auditTrailOffset: state.usersReducer.auditTrailOffset,
  offset: state.usersReducer.offset,
  column: state.usersReducer.column,
  order: state.usersReducer.order,
});
const mapDispatchToProps = {
  clearError,
  fetchUser,
  fetchUsers,
  setIsSingleUserWorkflow,
  saveAuditTrailComment,
  resetAuditTrailPagination,
  fetchEventHistory,
  resetLoyaltyUser,
  resetEventLot,
};

const portletConfig = {
  tabs: ['User Details', 'User Status', 'Audit Trail', 'Event Log'],
  actionsLabel: 'Actions',
  actions: [],
};

const UserView = ({
  clearError,
  isWorkflowActive,
  user,
  userDbId,
  searchParams,
  errorCode,
  fetchUser,
  // eslint-disable-next-line no-unused-vars
  fetchUsers,
  resetAuditTrailPagination,
  isFetching,
  setIsSingleUserWorkflow,
  saveAuditTrailComment,
  auditTrailLimit,
  auditTrailOffset,
  offset,
  userSsoId,
  resetLoyaltyUser,
  resetEventLot,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [visible, setVisible] = React.useState(false);

  // This effect and refs focuses the comment form field upon displaying it and
  // cleans up interval upon unmount or close modal
  const commentRef = useRef();
  const intervalRef = useRef();
  useEffect(() => {
    if (visible) {
      setCommentFocusIntervalRef(commentRef, intervalRef);
    } else {
      clearIntervalRef(intervalRef);
    }
    return () => clearIntervalRef(intervalRef);
  }, [visible]);

  const history = useHistory();
  const timeoutId = useRef();

  // if the user is purged, redirect to purged page
  // once the workflow modal is closed,
  // after half a second
  useEffect(() => {
    if (!isWorkflowActive && getProp(user, 'purged', false)) {
      if (!timeoutId.current) {
        timeoutId.current = setTimeout(
          () => history.push(`/purged-users/${user.id}`),
          500,
        );
      }
    }
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [user, isWorkflowActive]);

  const [form] = Form.useForm();
  const { id } = useParams();
  const location = useLocation();
  // for dynamic 'back' button on details page
  const basePath = `/${location.pathname.split('/')[1]}`;
  let originPath = basePath;
  let querystring;
  if (searchParams.length) {
    querystring = formToQueryString(searchParams);
    const offsetPath = offset > 0 ? `&offset=${offset}` : '';
    originPath = `${originPath}?${querystring}${offsetPath}`;
  }
  const userId = id;

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Prevent clearing of selected users array upon finishing workflow
  useEffect(() => {
    setIsSingleUserWorkflow();
  }, []);

  useEffect(() => () => {
    resetAuditTrailPagination();
    resetLoyaltyUser();
    resetEventLot();
  }, []);

  useEffect(() => {
    fetchUser({ userId });
  }, [userId]);

  const showModal = () => {
    setVisible(true);
  };

  const onSubmit = (values) => {
    const comment = getProp(values, 'audit-log-comment');
    console.log('at button', userDbId);
    saveAuditTrailComment({
      comment,
      userDbId,
      auditTrailLimit,
      auditTrailOffset,
    });
    setVisible(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  if (errorCode) {
    const redirectObj = {
      pathname: basePath,
    };
    if (querystring) {
      redirectObj.search = `?${querystring}`;
    }
    console.log('redirect', redirectObj);
    clearError();
    return <Redirect to={redirectObj} />;
  }
  return (
    <div className="row">
      <div className="col-lg-12 col-xl-12 order-lg-1 order-xl-1">
        <SingleActionsWorkflowModal />
        <BulkActionsWorkflowModal />
        <Modal
          title="Please add a comment"
          centered
          visible={visible}
          okText="Submit"
          cancelText="Cancel"
          onOk={form.submit}
          onCancel={handleCancel}
          destroyOnClose
        >
          <Form form={form} onFinish={onSubmit} preserve={false}>
            <Form.Item name="audit-log-comment">
              <Input.TextArea
                className="comment-text-area"
                placeholder="add optional comment here"
                rows={4}
                ref={commentRef}
                maxLength={280}
                autoComplete="off"
              />
            </Form.Item>
          </Form>
        </Modal>
        <Portlet
          config={portletConfig}
          activeTab={activeTab}
          handleTabClick={handleTabClick}
        >
          {activeTab === 2 && !isFetching && (
            <div className="action-btn">
              <button
                onClick={() => {
                  showModal(true);
                }}
                className="btn btn-secondary"
              >
                Add Comment
              </button>
            </div>
          )}
          {activeTab === 0 && (
            <Loader isLoading={isFetching}>
              <Details user={user} originPath={originPath} />
            </Loader>
          )}
          {activeTab === 1 && (
            <Loader isLoading={isFetching}>
              <Status user={user} originPath={originPath} />
            </Loader>
          )}
          {activeTab === 2 && (
            <Loader isLoading={isFetching}>
              <AuditTrail userDbId={userDbId} originPath={originPath} />
            </Loader>
          )}
          {activeTab === 3 && (
            <Loader isLoading={isFetching}>
              <EventLog userSsoId={userSsoId} originPath={originPath} />
            </Loader>
          )}
        </Portlet>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UserView);



// WEBPACK FOOTER //
// ./src/js/views/user/index.jsx